import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from './context/AuthContext';
import axiosInstance from './axiosInstance';
import './FunctionAssignmentStyles.css';
import CompanySelector from './components/CompanySelector';
import ProtectedComponent from './ProtectedComponent';

const FunctionAssignment = () => {
  const { user } = useContext(AuthContext);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [functions, setFunctions] = useState({});
  const [originalFunctions, setOriginalFunctions] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [webhookType, setWebhookType] = useState('restAPI');
  const [webhookCheckboxes, setWebhookCheckboxes] = useState({
    labelStatus: false,
    labelButtonPress: false,
    productReconciliation: false
  });
  const [showParameterModal, setShowParameterModal] = useState(false);
  const [selectedParameters, setSelectedParameters] = useState({});
  const [parameterSearchResults, setParameterSearchResults] = useState([]);
  const [isParameterSearch, setIsParameterSearch] = useState(false);
  const [searchingParameters, setSearchingParameters] = useState(false);
  const [activeTab, setActiveTab] = useState('bulk');
  const [bulkData, setBulkData] = useState([]);
  const [loadingBulk, setLoadingBulk] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [bulkChanges, setBulkChanges] = useState({});
  const [originalBulkData, setOriginalBulkData] = useState([]);
  const [filteredBulkData, setFilteredBulkData] = useState([]);
  const [showBulkParameterModal, setShowBulkParameterModal] = useState(false);
  const [bulkSelectedParameters, setBulkSelectedParameters] = useState({});
  const [searchingBulkParameters, setSearchingBulkParameters] = useState(false);
  const [parameterSearchMode, setParameterSearchMode] = useState('enabled'); // 'enabled' or 'disabled'
  const [showBulkConfirmModal, setShowBulkConfirmModal] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successCount, setSuccessCount] = useState(0);
  const [showFailureModal, setShowFailureModal] = useState(false);
  const [failureData, setFailureData] = useState({ success: 0, failed: 0 });
  const [failedStores, setFailedStores] = useState([]);
  const [searchFilteredData, setSearchFilteredData] = useState([]);
  const [selectedBulkCompanyCodes, setSelectedBulkCompanyCodes] = useState([]); // For bulk management multi-select
  const [bulkSearchText, setBulkSearchText] = useState(''); // For bulk management search text
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' }); // For column sorting

  // Store Lookup state
  const [storeLookupSearch, setStoreLookupSearch] = useState('');
  const [companySuggestions, setCompanySuggestions] = useState([]); // Companies that match search
  const [selectedLookupCompany, setSelectedLookupCompany] = useState(null); // Selected company for lookup
  const [companyStores, setCompanyStores] = useState([]); // Stores for selected company
  const [loadingStores, setLoadingStores] = useState(false);
  const [storesError, setStoresError] = useState('');
  const [hoveredStore, setHoveredStore] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showBulkSuggestions, setShowBulkSuggestions] = useState(false); // For bulk management dropdown
  const [allCompanies, setAllCompanies] = useState([]); // Cache of all companies from API
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  // Store Config state (for By Store tab)
  const [storeConfigData, setStoreConfigData] = useState([]); // Array of store configs with their settings
  const [loadingConfigs, setLoadingConfigs] = useState(false);
  const [configLoadProgress, setConfigLoadProgress] = useState({ current: 0, total: 0 }); // Track loading progress
  const [storeEditMode, setStoreEditMode] = useState(false);
  const [storeConfigChanges, setStoreConfigChanges] = useState({});
  const [originalStoreConfigs, setOriginalStoreConfigs] = useState([]);
  const [savingStoreConfig, setSavingStoreConfig] = useState(false);
  const [selectedStores, setSelectedStores] = useState([]); // Stores selected for bulk update
  const [bulkUpdateValues, setBulkUpdateValues] = useState({}); // Values to update in bulk
  const [concurrentUpdating, setConcurrentUpdating] = useState(false);

  const fetchCompanies = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      console.log('Fetching companies from backend...');

      const { data } = await axiosInstance.get('/function-assignment/companies');

      console.log('Companies data:', data);
      // The API returns {companyList: [...], responseCode: 200, responseMessage: 'SUCCESS'}
      const companyList = data.companyList || [];

      // Filter companies that have both code and name, and sort by code
      const filteredCompanies = companyList
        .filter(company => company.code && company.name)
        .sort((a, b) => (a.code || '').localeCompare(b.code || ''));
      console.log('Loaded companies:', filteredCompanies.length);

      setCompanies(filteredCompanies);
    } catch (err) {
      setError('Failed to fetch companies: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Fetch companies on mount (uses backend route with user's SaaS token from database)
    fetchCompanies();
  }, [fetchCompanies]);

  const fetchFunctions = async (companyCode) => {
    try {
      setLoading(true);
      setError('');

      const { data } = await axiosInstance.get(`/function-assignment/functions/${companyCode}`);

      // Remove response fields that shouldn't be edited
      const { responseCode, responseMessage, ...functionData } = data;
      setFunctions(functionData);
      setOriginalFunctions(functionData); // Store original state for change detection
    } catch (err) {
      setError('Failed to fetch functions: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllCompanies = async () => {
    if (allCompanies.length > 0) return allCompanies; // Already cached

    try {
      setLoadingCompanies(true);
      console.log('Fetching all companies from SaaS API...');

      const response = await axiosInstance.get('/function-assignment/all-companies');

      console.log('All companies response:', response.data);

      if (response.data && response.data.companyList) {
        const companies = response.data.companyList
          .filter(c => c.code && c.name)
          .map(c => ({
            companyCode: c.code,
            companyName: c.name,
            id: c.id,
            currentstorecount: c.currentstorecount || 0
          }))
          .sort((a, b) => a.companyCode.localeCompare(b.companyCode));

        setAllCompanies(companies);
        console.log('Cached companies:', companies.length);
        return companies;
      }
      return [];
    } catch (err) {
      console.error('Error fetching all companies:', err);
      setStoresError('Failed to fetch companies: ' + (err.response?.data?.message || err.message));
      return [];
    } finally {
      setLoadingCompanies(false);
    }
  };

  const searchCompanies = async (searchText) => {
    // If empty search text, don't show anything
    if (!searchText || searchText.length === 0) {
      setCompanySuggestions([]);
      setShowSuggestions(false);
      return;
    }

    // Ensure companies are loaded
    if (allCompanies.length === 0 && !loadingCompanies) {
      await fetchAllCompanies();
      return; // Will be called again after companies load
    }

    // If search text is less than 2 characters, don't show suggestions yet
    if (searchText.length < 2) {
      setCompanySuggestions([]);
      setShowSuggestions(false);
      return;
    }

    // Filter from cached companies for 2+ characters
    const searchLower = searchText.toLowerCase();
    const matches = allCompanies.filter(company =>
      company.companyCode.toLowerCase().includes(searchLower) ||
      company.companyName.toLowerCase().includes(searchLower)
    );

    setCompanySuggestions(matches.slice(0, 10)); // Limit to 10 suggestions
    setShowSuggestions(matches.length > 0);
  };

  const handleInputFocus = async () => {
    // Fetch companies if not already loaded
    let companies = allCompanies;
    if (allCompanies.length === 0 && !loadingCompanies) {
      companies = await fetchAllCompanies();
    }

    // Show ALL companies on focus (scrollable dropdown)
    if (companies && companies.length > 0) {
      setCompanySuggestions(companies); // Show ALL, not just 10
      setShowSuggestions(true);
    }
  };

  const handleBulkInputFocus = async () => {
    // Fetch companies if not already loaded
    let companies = allCompanies;
    if (allCompanies.length === 0 && !loadingCompanies) {
      companies = await fetchAllCompanies();
    }

    // Show ALL companies on focus (scrollable dropdown)
    if (companies && companies.length > 0) {
      setCompanySuggestions(companies); // Reuse same suggestions array
      setShowBulkSuggestions(true);
    }
  };

  const handleCompanySelectForLookup = async (company) => {
    setSelectedLookupCompany(company);
    setStoreLookupSearch('');
    setShowSuggestions(false);
    setCompanySuggestions([]);
    setStoreConfigData([]);
    setStoreConfigChanges({});
    setSelectedStores([]);
    setBulkUpdateValues({});

    // Fetch store list AND configurations automatically
    try {
      setLoadingStores(true);
      setStoresError('');
      setCompanyStores([]);

      console.log('Fetching stores for company:', company.companyCode);

      const response = await axiosInstance.get(`/function-assignment/proxy-stores?company=${company.companyCode}`);

      console.log('Stores response:', response.data);

      if (response.data && response.data.stores) {
        const stores = response.data.stores;
        setCompanyStores(stores);

        // Automatically fetch configurations
        await fetchStoreConfigurations(company.companyCode, stores);
      } else {
        setStoresError('No stores found for this company');
      }
    } catch (err) {
      console.error('Error fetching stores:', err);
      setStoresError('Failed to fetch stores: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoadingStores(false);
    }
  };

  // Helper: normalize values (string "true"/"false" -> bool, numeric strings -> int)
  const parseValue = (val) => {
    if (typeof val !== 'string') return val;
    const lower = val.toLowerCase().trim();
    if (lower === 'true') return true;
    if (lower === 'false') return false;
    const num = parseInt(val, 10);
    if (!isNaN(num) && String(num) === val.trim()) return num;
    return val;
  };

  const fetchStoreConfigurations = async (companyCode, stores) => {
    try {
      setLoadingConfigs(true);
      setConfigLoadProgress({ current: 0, total: stores.length });
      console.log(`Fetching configurations for ${stores.length} stores`);

      // Unwanted fields to remove (matching desktop app)
      const UNWANTED_FIELDS = [
        'updatePageUnlock',
        'whiteListAutoInterval',
        'whitelistAutoInterval',
        'whiteListEnable',
        'acsEnable'
      ];

      // Fetch configs in batches of 5 (prevents timeout)
      const BATCH_SIZE = 5;
      const configResults = [];
      let completedCount = 0;

      for (let i = 0; i < stores.length; i += BATCH_SIZE) {
        const batch = stores.slice(i, i + BATCH_SIZE);
        const batchPromises = batch.map(async (store) => {
          try {
            const response = await axiosInstance.get(
              `/function-assignment/store-config/${companyCode}/${store.store}`
            );

            if (response.data && response.data.storeConfig) {
              const cfg = { ...response.data.storeConfig };

              // Remove unwanted fields
              UNWANTED_FIELDS.forEach(field => delete cfg[field]);

              // Normalize all config values
              Object.keys(cfg).forEach(key => {
                cfg[key] = parseValue(cfg[key]);
              });

              // Update progress
              completedCount++;
              setConfigLoadProgress({ current: completedCount, total: stores.length });

              return {
                storeCode: store.store,
                storeName: response.data.storeName || store.storeName,
                fullData: response.data, // Keep entire response for PUT
                originalConfig: { ...cfg }, // Store original for delta detection
                ...cfg // Spread normalized config fields for display
              };
            }
            completedCount++;
            setConfigLoadProgress({ current: completedCount, total: stores.length });
            return null;
          } catch (error) {
            console.error(`Error fetching config for store ${store.store}:`, error.message);
            completedCount++;
            setConfigLoadProgress({ current: completedCount, total: stores.length });
            return null;
          }
        });

        const batchResults = await Promise.all(batchPromises);
        configResults.push(...batchResults.filter(r => r !== null));
      }

      console.log(`Fetched ${configResults.length} store configurations`);
      setStoreConfigData(configResults);
      setOriginalStoreConfigs(JSON.parse(JSON.stringify(configResults)));
    } catch (error) {
      console.error('Error fetching store configurations:', error);
    } finally {
      setLoadingConfigs(false);
      setConfigLoadProgress({ current: 0, total: 0 });
    }
  };

  const clearCompanySelection = () => {
    setSelectedLookupCompany(null);
    setCompanyStores([]);
    setStoresError('');
    setStoreConfigData([]);
    setStoreConfigChanges({});
    setStoreEditMode(false);
  };

  // Search on every keystroke - shows all on empty, filters after 3 characters
  useEffect(() => {
    searchCompanies(storeLookupSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeLookupSearch, allCompanies]);

  // Filter bulk suggestions when search term changes
  useEffect(() => {
    if (activeTab === 'bulk' && showBulkSuggestions && allCompanies.length > 0) {
      if (searchTerm && searchTerm.length >= 2) {
        const searchLower = searchTerm.toLowerCase();
        const matches = allCompanies.filter(company =>
          company.companyCode.toLowerCase().includes(searchLower) ||
          company.companyName.toLowerCase().includes(searchLower)
        );
        setCompanySuggestions(matches.slice(0, 50)); // Limit to 50 for performance
      } else if (searchTerm.length === 0) {
        setCompanySuggestions(allCompanies.slice(0, 50)); // Show first 50 when empty
      } else {
        setCompanySuggestions([]); // Hide for 1 character
      }
    }
  }, [searchTerm, allCompanies, activeTab, showBulkSuggestions]);

  const handleCompanySelect = (companyCode, companyName) => {
    setSelectedCompany(companyCode);
    setSearchTerm(''); // Clear search when company is selected
    setShowDropdown(false);
    if (companyCode) {
      fetchFunctions(companyCode);
    } else {
      setFunctions({});
      setOriginalFunctions({});
    }
  };

  const clearSelection = () => {
    setSelectedCompany('');
    setSearchTerm('');
    setFunctions({});
    setOriginalFunctions({});
    setShowDropdown(false);
  };

  const handleFunctionToggle = (functionKey) => {
    setFunctions(prev => {
      const newFunctions = { ...prev };

      if (['squarePos', 'toastPos', 'heartlandPos'].includes(functionKey)) {
        newFunctions.squarePos = false;
        newFunctions.toastPos = false;
        newFunctions.heartlandPos = false;
        newFunctions[functionKey] = true;
        return newFunctions;
      }

      // Handle number-based toggles
      if (typeof prev[functionKey] === 'number') {
        newFunctions[functionKey] = prev[functionKey] > 0 ? 0 : 7; // Toggle between 0 (off) and 7 (on with default)
        return newFunctions;
      }

      newFunctions[functionKey] = !prev[functionKey];
      return newFunctions;
    });
  };

  const handleWebhookTypeChange = (type) => {
    setWebhookType(type);
    setWebhookCheckboxes({
      labelStatus: false,
      labelButtonPress: false,
      productReconciliation: false
    });
  };

  const handleWebhookCheckbox = (checkbox) => {
    setWebhookCheckboxes(prev => ({
      ...prev,
      [checkbox]: !prev[checkbox]
    }));
  };

  const handleSave = async () => {
    if (!selectedCompany) {
      setError('Please select a company first');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccessMessage('');

      await axiosInstance.post(`/function-assignment/functions/${selectedCompany}`, functions);

      setSuccessMessage('Functions updated successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Failed to save functions: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const openParameterModal = () => {
    const allFunctions = Object.keys(categorizedFunctions).reduce((acc, category) => {
      categorizedFunctions[category].forEach(func => {
        acc[func] = false;
      });
      return acc;
    }, {});
    setSelectedParameters(allFunctions);
    setShowParameterModal(true);
  };

  const toggleParameter = (param) => {
    setSelectedParameters(prev => ({
      ...prev,
      [param]: !prev[param]
    }));
  };

  const searchByParameters = async () => {
    const selected = Object.keys(selectedParameters).filter(key => selectedParameters[key]);
    if (selected.length === 0) {
      setError('Please select at least one parameter to search');
      return;
    }

    try {
      setSearchingParameters(true);
      setError('');
      setShowParameterModal(false);

      const companyList = companies.length > 0 ? companies : [];
      const matchingCompanies = [];

      for (const company of companyList) {
        try {
          const { data } = await axiosInstance.get(`/function-assignment/functions/${company.code}`);
          const { responseCode, responseMessage, ...functionData } = data;

          const hasAllSelected = selected.every(param => {
            if (typeof functionData[param] === 'number') {
              return functionData[param] > 0;
            }
            return functionData[param] === true;
          });

          if (hasAllSelected) {
            matchingCompanies.push(company);
          }
        } catch (err) {
          console.error(`Error fetching functions for ${company.code}:`, err);
        }
      }

      setParameterSearchResults(matchingCompanies);
      setIsParameterSearch(true);
      setSearchingParameters(false);

      if (matchingCompanies.length === 0) {
        setError('No stores found with all selected parameters enabled');
      } else {
        setShowDropdown(true);
      }
    } catch (err) {
      setError('Failed to search by parameters: ' + err.message);
      setSearchingParameters(false);
    }
  };

  const clearParameterSearch = () => {
    setIsParameterSearch(false);
    setParameterSearchResults([]);
    setSelectedParameters({});
  };

  const openBulkParameterModal = () => {
    const allFunctions = Object.keys(categorizedFunctions).reduce((acc, category) => {
      categorizedFunctions[category].forEach(func => {
        acc[func] = false;
      });
      return acc;
    }, {});
    setBulkSelectedParameters(allFunctions);
    setShowBulkParameterModal(true);
  };

  const toggleBulkParameter = (param) => {
    setBulkSelectedParameters(prev => ({
      ...prev,
      [param]: !prev[param]
    }));
  };

  const searchBulkByParameters = async () => {
    const selected = Object.keys(bulkSelectedParameters).filter(key => bulkSelectedParameters[key]);
    if (selected.length === 0) {
      setError('Please select at least one parameter to search');
      return;
    }

    try {
      setSearchingBulkParameters(true);
      setError('');
      setShowBulkParameterModal(false);

      const isSearchingEnabled = parameterSearchMode === 'enabled';

      const matchingStores = bulkData.filter(store => {
        return selected.every(param => {
          const value = store[param];
          const isEnabled = typeof value === 'number' ? value > 0 : value === true;
          // If searching for enabled, return true if enabled; if searching for disabled, return true if disabled
          return isSearchingEnabled ? isEnabled : !isEnabled;
        });
      });

      setFilteredBulkData(matchingStores);
      setSearchingBulkParameters(false);

      if (matchingStores.length === 0) {
        setError(`No stores found with all selected parameters ${parameterSearchMode}`);
      }
    } catch (err) {
      setError('Failed to search by parameters: ' + err.message);
      setSearchingBulkParameters(false);
    }
  };

  const clearBulkParameterFilter = () => {
    setFilteredBulkData([]);
    setBulkSelectedParameters({});
  };

  // Handle column sorting
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Get sorted data
  const getSortedData = (data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      // Handle boolean/checkbox values (true/1 = checked, false/0 = unchecked)
      if (typeof aVal === 'boolean' || typeof bVal === 'boolean' ||
          aVal === 0 || aVal === 1 || bVal === 0 || bVal === 1) {
        aVal = aVal === true || aVal === 1 ? 1 : 0;
        bVal = bVal === true || bVal === 1 ? 1 : 0;
      }

      // Handle string comparison (case-insensitive)
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      // Handle null/undefined
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const exportToCSV = () => {
    // Determine which data to export based on filters
    const dataToExport = (selectedBulkCompanyCodes.length > 0 || bulkSearchText.trim().length >= 2)
      ? searchFilteredData
      : (filteredBulkData.length > 0 ? filteredBulkData : bulkData);

    if (dataToExport.length === 0) {
      alert('No data to export');
      return;
    }

    // Build CSV header
    const headers = ['Store Code', 'Store Name'];
    Object.keys(categorizedFunctions).forEach(category => {
      categorizedFunctions[category].forEach(funcKey => {
        headers.push(getDisplayName(funcKey));
      });
    });

    // Build CSV rows
    const rows = dataToExport.map(store => {
      const row = [store.code, store.name];
      Object.keys(categorizedFunctions).forEach(category => {
        categorizedFunctions[category].forEach(funcKey => {
          const value = store[funcKey];
          // Convert boolean/number to readable format
          if (typeof value === 'boolean') {
            row.push(value ? 'Yes' : 'No');
          } else if (typeof value === 'number') {
            row.push(value === 1 ? 'Yes' : 'No');
          } else {
            row.push(value || '');
          }
        });
      });
      return row;
    });

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `function-assignment-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportStoreConfigToCSV = () => {
    if (!storeConfigData || storeConfigData.length === 0) {
      alert('No store configuration data to export');
      return;
    }

    // Get config keys from the first store
    const configKeys = storeConfigData[0].fullData?.storeConfig
      ? Object.keys(storeConfigData[0].fullData.storeConfig)
      : [];

    if (configKeys.length === 0) {
      alert('No configuration fields available to export');
      return;
    }

    // Build CSV header
    const headers = ['Store Code', 'Store Name', ...configKeys.map(key => getStoreConfigDisplayName(key))];

    // Build CSV rows
    const rows = storeConfigData.map(store => {
      const row = [store.storeCode, store.storeName || ''];
      configKeys.forEach(configKey => {
        const value = store[configKey];
        if (typeof value === 'boolean') {
          row.push(value ? 'Enabled' : 'Disabled');
        } else if (value === 1 || value === 0) {
          row.push(value === 1 ? 'Enabled' : 'Disabled');
        } else {
          row.push(value !== null && value !== undefined ? value : '');
        }
      });
      return row;
    });

    // Combine headers and rows with proper escaping
    const csvContent = [
      headers.map(h => `"${String(h).replace(/"/g, '""')}"`).join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const companyCode = selectedLookupCompany?.companyCode || 'store-config';
    link.setAttribute('href', url);
    link.setAttribute('download', `${companyCode}-store-config-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportCompanyConfigJSON = async (companyCode, companyName) => {
    try {
      const response = await axiosInstance.get(`/function-assignment/export/${companyCode}`);

      if (response.data && response.data.success) {
        const exportData = {
          companyCode: response.data.companyCode,
          companyName: companyName || companyCode,
          exportedAt: response.data.exportedAt,
          exportedBy: response.data.exportedBy,
          config: response.data.config
        };

        const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `${companyCode}-function-config-${new Date().toISOString().split('T')[0]}.json`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      console.error('Error exporting config:', err);
      alert('Failed to export config: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleBulkSave = async () => {
    setShowBulkConfirmModal(false);
    setBulkSaving(true);
    setError('');
    setSuccessMessage('');

    try {
      // Step 1: Create snapshot of current state BEFORE making changes
      console.log('[Bulk Save] Creating snapshot before changes...');

      const companiesSnapshot = {};
      const affectedCompanies = [];
      const changedFields = new Set();

      // Build snapshot from original data (state before changes)
      Object.keys(bulkChanges).forEach(storeCode => {
        const originalStore = originalBulkData.find(s => s.code === storeCode);
        if (originalStore) {
          companiesSnapshot[storeCode] = originalStore;
          affectedCompanies.push({
            code: originalStore.code,
            name: originalStore.name
          });

          // Track which fields are being changed
          Object.keys(bulkChanges[storeCode]).forEach(field => {
            changedFields.add(field);
          });
        }
      });

      // Create snapshot via API
      try {
        const snapshotResponse = await axiosInstance.post('/config-snapshots', {
          userId: user?.email || 'unknown',
          userName: user?.name || user?.email || 'Unknown User',
          action: 'bulk_save',
          companiesSnapshot,
          affectedCompanies,
          changedFields: Array.from(changedFields),
          notes: `Bulk save affecting ${affectedCompanies.length} companies`
        });

        console.log('[Bulk Save] Snapshot created:', snapshotResponse.data.snapshotId);
      } catch (snapshotError) {
        console.error('[Bulk Save] Failed to create snapshot:', snapshotError);
        // Continue with save even if snapshot fails - log the error
        alert('Warning: Failed to create backup snapshot. Continue anyway?');
        // You could add a confirmation here if desired
      }

      // Step 2: Proceed with actual saves
      let successCount = 0;
      let failCount = 0;
      const failedStoresList = [];
      const successfulStoresList = [];

      for (const [storeCode, storeData] of Object.entries(bulkChanges)) {
        try {
          console.log(`[Bulk Save] Saving functions for ${storeCode}:`, storeData);
          const response = await axiosInstance.post(`/function-assignment/functions/${storeCode}`, storeData);
          console.log(`[Bulk Save] Success for ${storeCode}:`, response.data);
          successCount++;
          successfulStoresList.push(storeCode);
        } catch (err) {
          console.error(`[Bulk Save] Failed to save ${storeCode}:`, err.response?.data || err.message);
          failCount++;
          failedStoresList.push(storeCode);
        }
      }

      setBulkSaving(false);
      if (failCount === 0) {
        setSuccessCount(successCount);
        setShowSuccessModal(true);
        setBulkChanges({});
        setEditMode(false);
        setOriginalBulkData(JSON.parse(JSON.stringify(bulkData)));
        setTimeout(() => {
          setShowSuccessModal(false);
        }, 2000);
      } else {
        setFailureData({ success: successCount, failed: failCount });
        setFailedStores(failedStoresList);
        setShowFailureModal(true);

        // Exit edit mode and clear changes
        setBulkChanges({});
        setEditMode(false);

        // Reload data to show actual current state
        await loadBulkData();

        setTimeout(() => {
          setShowFailureModal(false);
          setFailedStores([]);
        }, 2000);
      }
    } catch (error) {
      console.error('[Bulk Save] Unexpected error:', error);
      setBulkSaving(false);
      setError('An unexpected error occurred during save');
    }
  };

  const loadBulkData = async () => {
    try {
      setLoadingBulk(true);
      setError('');

      console.log('Loading bulk data using batch endpoint...');

      const { data } = await axiosInstance.get('/function-assignment/bulk-functions');

      if (data && data.success && data.data) {
        const allData = data.data.map(item => ({
          code: item.companyCode,
          name: item.companyName,
          error: item.error, // Include error if present
          ...item
        }));

        setBulkData(allData);
        setOriginalBulkData(JSON.parse(JSON.stringify(allData)));
        console.log(`Loaded ${allData.length} companies in bulk`);
      } else {
        setError('Invalid response from bulk functions endpoint');
      }

      setLoadingBulk(false);
    } catch (err) {
      console.error('Error loading bulk data:', err);
      setError('Failed to load bulk data: ' + (err.response?.data?.error || err.message));
      setLoadingBulk(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'bulk' && companies.length > 0 && bulkData.length === 0) {
      loadBulkData();
    }
  }, [activeTab, companies]);

  // Load all companies once on page load for both tabs
  useEffect(() => {
    if (allCompanies.length === 0 && !loadingCompanies) {
      fetchAllCompanies();
    }
  }, []);

  // Combined filtering for bulk data (company codes + search text)
  useEffect(() => {
    if (activeTab === 'bulk') {
      let filtered = [...bulkData];

      console.log('🔍 BULK FILTER DEBUG:', {
        activeTab,
        selectedBulkCompanyCodes,
        bulkSearchText,
        bulkDataLength: bulkData.length
      });

      // Filter by selected company codes (checkbox selections)
      if (selectedBulkCompanyCodes.length > 0) {
        console.log('✅ Filtering by selected company codes:', selectedBulkCompanyCodes);
        filtered = filtered.filter(store =>
          selectedBulkCompanyCodes.includes(store.code)
        );
      }
      // ALSO filter by search text (real-time typing filter)
      else if (bulkSearchText && bulkSearchText.trim().length >= 2) {
        console.log('✅ Filtering by search text:', bulkSearchText);
        const searchLower = bulkSearchText.toLowerCase();
        filtered = filtered.filter(store =>
          store.code.toLowerCase().includes(searchLower) ||
          store.name.toLowerCase().includes(searchLower)
        );
        console.log(`✅ Filtered ${bulkData.length} → ${filtered.length} companies:`, filtered.map(s => s.code));
      }
      // If no filter active, show all companies
      else {
        console.log('⚪ No filter active, showing all companies');
      }

      console.log('📊 Setting searchFilteredData to', filtered.length, 'items');
      setSearchFilteredData(filtered);
    } else {
      setSearchFilteredData([]);
    }
  }, [selectedBulkCompanyCodes, bulkSearchText, bulkData, activeTab]);

  const filteredCompanies = isParameterSearch
    ? parameterSearchResults.filter(company =>
        (company.name && company.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (company.code && company.code.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    : companies.filter(company =>
        (company.name && company.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (company.code && company.code.toLowerCase().includes(searchTerm.toLowerCase()))
      );

  const hasChanges = JSON.stringify(functions) !== JSON.stringify(originalFunctions);

  const fieldDisplayNames = {
    'assignWithoutArticle': 'Assign Without Products',
    'appendAssign': 'Product append to existing Assign',
    'positionIdExtraction': 'Product Position Extraction',
    'articleDataSchedule': 'Product Level Schedule',
    'preserveArticleFiles': 'Preserve Product Files',
    'articleScheduleAllowedDays': 'Article Schedule Allowed Days',
    'templateMapping': 'Template Mapping',
    'templateColors': 'Template Color',
    'templateLevels': 'Template Levels',
    'shelfBasedAssign': 'Store Based Template',
    'customTemplates': 'Custom Template',
    'masterBoxStatus': 'Master Box',
    'wifiTags': 'WiFi Tag',
    'ciscoMerakiApIntegration': 'Cisco Meraki AP Integration',
    'imgPushOverrideOnArticleUpdate': 'Block Product Update After Image Push',
    'articleImagePush': 'Article Image Push',
    'labelOTAStatus': 'Label OTA',
    'cameraOTAStatus': 'Camera OTA',
    'squarePos': 'Square POS Integration',
    'toastPos': 'Toast POS Integration',
    'heartlandPos': 'Heartland POS Integration',
    'whiteListEnable': 'White List',
    'whiteListForceAssign': 'Whitelist Pending Auto Assign',
    'webhooks': 'Webhooks',
    'webhookRestAPI': 'Webhook REST API',
    'webhookAzureEventHub': 'Webhook Azure Event Hub',
    'powerRail': 'Power Rail',
    'powerRailAutoRegistration': 'Power Rail Auto Registration',
    'rfidScanner': 'RFID Scanner',
    'visionRobot': 'Vision Robot',
    'sensors': 'Sensors',
    'newton7Segment': 'Newton Segment',
    'labelStatus': 'Label Status',
    'labelButtonPress': 'Label Button Press',
    'softwareUpdateNotice': 'Software Update Notice',
    'gatewayOfflineNotifications': 'Gateway Offline Notifications',
    'labelNotifications': 'Label Notifications',
    'licenseRemainder': 'License Remainder',
    'dailyReport': 'Daily Report',
    'customerOverviewReport': 'Customer Overview Report',
    'sensorsAlertNotification': 'Sensors Alert Notification',
    'monthlyReports': 'Monthly Reports',
    'acsEnable': 'Acs Enable',
    'storeLicense': 'Store License',
    'jsCodeSnippets': 'Js Code Snippets',
    'customDashboardLogo': 'Custom Dashboard Logo',
    'productReconciliation': 'Product Reconciliation',
    'reportConfigByAdmin': 'Report Config By Admin',
    'tandlSpaceTrimming': 'Article Data Space Trimming',
    'nfcChanges': 'NFC Changes',
    'storeSchedule': 'Store Schedule',
    'meetingSolution': 'Meeting Room Solution',
    'orangeColor': 'Orange Color',
    'ledPromotion': 'Led Promotion',
    'exactLabelPreview': 'Exact Label Preview',
    'atensystemsMap': 'Atensystems Map',
    'outOfStock': 'Out of Stock',
    'forceAliveSignal': 'On Demand Alive',
    'wastePrevention': 'Waste Prevention',
    'multiColorLED': 'Multi Color LED',
    'userBasedTemplates': 'User Based Templates',
    'visionCamera': 'Vision Camera',
    'aimsManagerDeviceLimit': 'Aims Manager Device Limit',
    'retentionPeriod': 'Retention Period (days)',
    'nfcDefaultUrl': 'NFC Default URL',
    'labelOTAStartDate': 'Label OTA Start Date',
    'labelOTAEndDate': 'Label OTA End Date',
    'cameraOTAStartDate': 'Camera OTA Start Date',
    'cameraOTAEndDate': 'Camera OTA End Date'
  };

  const getDisplayName = (key) => {
    return fieldDisplayNames[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
  };

  // Helper to determine field type for proper input rendering
  const getFieldType = (funcKey, value) => {
    // Parent fields that have associated day values (to match SaaS UI)
    // When checked, a number input appears next to the checkbox for the day value
    if (funcKey === 'preserveArticleFiles') {
      return 'checkbox-number';
    }
    // Parent field with associated text value (NFC Changes + URL)
    // When checked, a text input appears next to the checkbox for the URL
    if (funcKey === 'nfcChanges') {
      return 'checkbox-text';
    }
    // OTA fields with date ranges - show checkmark/X and date range
    if (funcKey === 'labelOTAStatus' || funcKey === 'cameraOTAStatus') {
      return 'ota-dates';
    }
    // Number fields that represent toggle state (0 = disabled, >0 = enabled with value)
    // These fields combine the toggle and value in one number field
    if (funcKey === 'articleScheduleAllowedDays') {
      return 'number-toggle';
    }
    // Number-only conditional fields (days) - child fields that show as numbers
    if (funcKey === 'retentionPeriod') {
      return 'number';
    }
    // Boolean/number toggles
    if (typeof value === 'boolean' || typeof value === 'number') {
      return 'checkbox';
    }
    // Default to text
    return 'text';
  };

  // Map conditional fields to their parent toggles
  const conditionalFieldParents = {
    'retentionPeriod': 'preserveArticleFiles',
    // nfcDefaultUrl is now part of nfcChanges (checkbox-text type), not a separate child
    'labelOTAStartDate': 'labelOTAStatus',
    'labelOTAEndDate': 'labelOTAStatus',
    'cameraOTAStartDate': 'cameraOTAStatus',
    'cameraOTAEndDate': 'cameraOTAStatus',
    'webhookRestAPI': 'webhooks',
    'webhookAzureEventHub': 'webhooks',
    'labelStatus': 'webhooks',
    'labelButtonPress': 'webhooks',
    'productReconciliation': 'webhooks',
    'powerRailAutoRegistration': 'powerRail'
  };

  // Check if a field should be shown (based on parent toggle state)
  const shouldShowField = (funcKey, store) => {
    const parent = conditionalFieldParents[funcKey];
    if (!parent) {
      return true; // Not a conditional field, always show
    }
    // Check if parent is enabled
    const parentValue = store[parent];
    return typeof parentValue === 'number' ? parentValue > 0 : parentValue;
  };

  // Get placeholder text for fields
  const getPlaceholder = (funcKey) => {
    const placeholders = {
      'nfcDefaultUrl': 'Enter URL (e.g., https://example.com)',
      'retentionPeriod': 'Days',
      'articleScheduleAllowedDays': 'Days',
      'labelOTAStartDate': 'Select date',
      'labelOTAEndDate': 'Select date',
      'cameraOTAStartDate': 'Select date',
      'cameraOTAEndDate': 'Select date'
    };
    return placeholders[funcKey] || '';
  };

  const categorizedFunctions = {
    'PRODUCTS': [
      'assignWithoutArticle', 'appendAssign', 'positionIdExtraction',
      'articleDataSchedule', 'articleScheduleAllowedDays',
      'preserveArticleFiles',
      'tandlSpaceTrimming', 'nfcChanges'
    ],
    'TEMPLATE': [
      'templateMapping', 'templateColors', 'templateLevels',
      'shelfBasedAssign', 'customTemplates', 'userBasedTemplates',
      'exactLabelPreview'
    ],
    'BETA FEATURES': [
      'masterBoxStatus', 'wifiTags', 'ciscoMerakiApIntegration',
      'orangeColor', 'ledPromotion', 'atensystemsMap'
    ],
    'FEATURES': [
      'aimsManagerDeviceLimit', 'forceAliveSignal', 'newton7Segment',
      'jsCodeSnippets', 'customDashboardLogo', 'rfidScanner',
      'acsEnable', 'storeSchedule'
    ],
    'VISION': [
      'visionCamera', 'visionRobot'
    ],
    'MULTIFUNCTION': [
      'outOfStock', 'wastePrevention', 'multiColorLED'
    ],
    'IMAGE PUSH': [
      'articleImagePush', 'imgPushOverrideOnArticleUpdate'
    ],
    'OTA': [
      'labelOTAStatus',
      'cameraOTAStatus'
    ],
    'POS': [
      'squarePos', 'toastPos', 'heartlandPos'
    ],
    'WHITE LIST': [
      'whiteListEnable', 'whiteListForceAssign'
    ],
    'STORE': [
      'storeLicense'
    ],
    'REPORT': [
      'reportConfigByAdmin', 'dailyReport', 'customerOverviewReport', 'monthlyReports'
    ],
    'WEBHOOK': [
      'webhooks', 'webhookRestAPI', 'webhookAzureEventHub',
      'labelStatus', 'labelButtonPress', 'productReconciliation'
    ],
    'NOTIFICATIONS': [
      'softwareUpdateNotice', 'gatewayOfflineNotifications',
      'labelNotifications', 'licenseRemainder', 'sensorsAlertNotification'
    ],
    'MEETING ROOM SOLUTION': [
      'meetingSolution'
    ],
    'DEVICES': [
      'sensors', 'powerRail', 'powerRailAutoRegistration'
    ]
  };

  // Store Config display names for common fields
  const storeConfigDisplayNames = {
    aliveInterval: 'Alive Interval',
    lostTime: 'Lost Time',
    logoDisplayTime: 'Logo Display Time',
    beaconLossLimit: 'Beacon Loss Limit',
    reactivationRetryLimit: 'Reactivation Retry Limit',
    scanPeriodSet: 'Scan Period Set',
    batteryMode: 'Battery Mode',
    location: 'Location',
    aesEnable: 'AES Enable',
    acsEnable: 'ACS Enable',
    whiteListEnable: 'White List Enable',
    tapGoStartTime: 'Tap Go Start Time',
    tapGoEndTime: 'Tap Go End Time',
    flexibleDefault: 'Flexible Default',
    rebootRetryCount: 'Reboot Retry Count',
    updatePageUnlock: 'Update Page Unlock',
    refreshStart: 'Refresh Start',
    ipTimeServer: 'IP Time Server',
    pageBackTime: 'Page Back Time',
    rfidEnable: 'RFID Enable',
    whitelistAutoInterval: 'Whitelist Auto Interval'
  };

  const getStoreConfigDisplayName = (key) => {
    return storeConfigDisplayNames[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
  };

  const handleStoreConfigChange = (storeCode, field, value) => {
    setStoreConfigChanges(prev => ({
      ...prev,
      [storeCode]: {
        ...(prev[storeCode] || {}),
        [field]: value
      }
    }));

    // Update the display data
    setStoreConfigData(prev => prev.map(store => {
      if (store.storeCode === storeCode) {
        return { ...store, [field]: value };
      }
      return store;
    }));
  };

  const handleBulkStoreUpdate = async () => {
    if (selectedStores.length === 0) {
      alert('Please select at least one store');
      return;
    }

    // Check if any values are set
    const hasValues = Object.keys(bulkUpdateValues).some(key =>
      bulkUpdateValues[key] !== '' && bulkUpdateValues[key] !== null && bulkUpdateValues[key] !== undefined
    );

    if (!hasValues) {
      alert('Please set at least one configuration value to update');
      return;
    }

    try {
      setConcurrentUpdating(true);
      console.log(`Updating ${selectedStores.length} stores with values:`, bulkUpdateValues);

      // Process in batches of 5 concurrently (prevents timeout)
      const BATCH_SIZE = 5;
      const allResults = [];

      for (let i = 0; i < selectedStores.length; i += BATCH_SIZE) {
        const batch = selectedStores.slice(i, i + BATCH_SIZE);

        const batchPromises = batch.map(async (storeCode) => {
          try {
            const storeData = storeConfigData.find(s => s.storeCode === storeCode);
            if (!storeData || !storeData.fullData || !storeData.originalConfig) {
              return { success: false, storeCode, error: 'Store data not found' };
            }

            const fullData = storeData.fullData;
            const oldConfig = storeData.originalConfig;

            // Apply overrides from bulk update fields
            const configUpdates = {};
            Object.keys(bulkUpdateValues).forEach(key => {
              const val = bulkUpdateValues[key];
              if (val !== '' && val !== null && val !== undefined) {
                configUpdates[key] = parseValue(val);
              }
            });

            // Build new config by merging current config with updates
            const newConfig = { ...oldConfig, ...configUpdates };

            // *** DELTA ONLY *** - only send changed fields (matching desktop app)
            const changedFields = {};
            Object.keys(newConfig).forEach(key => {
              if (oldConfig[key] !== newConfig[key]) {
                changedFields[key] = newConfig[key];
              }
            });

            // Skip if no changes
            if (Object.keys(changedFields).length === 0) {
              console.log(`No changes for store ${storeCode}, skipping`);
              return { success: true, storeCode, skipped: true };
            }

            // SOLUM's PUT /store resets any OMITTED boolean storeConfig field to
            // false (verified on COR/NJT: editing refreshStart flipped whiteListEnable
            // true->false). A delta-only payload therefore silently disables whitelist
            // and any other boolean (POS/OTA/forceAssign/etc). Carry every current
            // boolean alongside the delta so untouched toggles are preserved.
            const storeConfigDelta = { ...changedFields };
            Object.keys(oldConfig).forEach((key) => {
              if (storeConfigDelta[key] === undefined && typeof oldConfig[key] === 'boolean') {
                storeConfigDelta[key] = oldConfig[key];
              }
            });

            // Build payload: top-level fields + storeConfig (delta + sticky booleans)
            const payload = {
              address: fullData.address,
              city: fullData.city,
              country: fullData.country,
              region: fullData.region,
              storeName: fullData.storeName,
              zoneId: fullData.zoneId,
              ipNtpServer: fullData.ipNtpServer,
              whiteListEnable: fullData.whiteListEnable,
              lbsEnabled: fullData.lbsEnabled,
              store: storeCode,
              storeConfig: storeConfigDelta
            };

            console.log(`Updating store ${storeCode} with delta:`, changedFields);

            await axiosInstance.put(
              `/function-assignment/store/${selectedLookupCompany.companyCode}/${storeCode}`,
              payload
            );

            // Update local state with new values
            setStoreConfigData(prev => prev.map(s => {
              if (s.storeCode === storeCode) {
                return {
                  ...s,
                  ...newConfig,
                  originalConfig: newConfig, // Update original for future deltas
                  fullData: {
                    ...s.fullData,
                    storeConfig: { ...s.fullData.storeConfig, ...changedFields }
                  }
                };
              }
              return s;
            }));

            return { success: true, storeCode };
          } catch (error) {
            console.error(`Failed to update store ${storeCode}:`, error);
            return { success: false, storeCode, error: error.message };
          }
        });

        const batchResults = await Promise.all(batchPromises);
        allResults.push(...batchResults);
      }

      const successCount = allResults.filter(r => r.success).length;
      const skippedCount = allResults.filter(r => r.skipped).length;
      const failedCount = allResults.filter(r => !r.success).length;

      if (failedCount === 0) {
        const msg = skippedCount > 0
          ? `Successfully updated ${successCount} store(s) (${skippedCount} had no changes)`
          : `Successfully updated ${successCount} store(s)`;
        alert(msg);
        setSelectedStores([]);
        setBulkUpdateValues({});
      } else {
        const failedStores = allResults.filter(r => !r.success).map(r => r.storeCode).join(', ');
        alert(`Updated ${successCount} store(s), but failed for: ${failedStores}`);
      }
    } catch (error) {
      console.error('Error updating stores:', error);
      alert('Failed to update stores');
    } finally {
      setConcurrentUpdating(false);
    }
  };

  const handleStoreSelection = (storeCode) => {
    setSelectedStores(prev => {
      if (prev.includes(storeCode)) {
        return prev.filter(s => s !== storeCode);
      } else {
        return [...prev, storeCode];
      }
    });
  };

  const handleSelectAllStores = () => {
    if (selectedStores.length === storeConfigData.length) {
      setSelectedStores([]);
    } else {
      setSelectedStores(storeConfigData.map(s => s.storeCode));
    }
  };

  // Auto-populate fields when single store is selected (matching desktop app behavior)
  useEffect(() => {
    if (selectedStores.length === 1 && storeConfigData.length > 0) {
      const selectedStore = storeConfigData.find(s => s.storeCode === selectedStores[0]);
      if (selectedStore && selectedStore.fullData && selectedStore.fullData.storeConfig) {
        const newValues = {};
        Object.keys(selectedStore.fullData.storeConfig).forEach(key => {
          const val = selectedStore[key];
          // For boolean fields, convert to string for display
          if (typeof val === 'boolean' || key.toLowerCase().includes('enable')) {
            newValues[key] = val ? 1 : 0;
          } else {
            newValues[key] = val;
          }
        });
        setBulkUpdateValues(newValues);
        console.log('Auto-populated fields from selected store:', selectedStores[0]);
      }
    } else if (selectedStores.length !== 1) {
      // Clear fields when multiple or no stores selected
      setBulkUpdateValues({});
    }
  }, [selectedStores, storeConfigData]);

  return (
    <ProtectedComponent functionCode="SA01" functionName="Function Assignment">
      <div className="function-assignment-container">
      <header className="function-assignment-header">
        <h1>Function Assignment</h1>
        <p>Manage function settings for SaaS companies</p>
      </header>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="success-message">
          {successMessage}
        </div>
      )}

      <div className="tab-container">
        <button
          className={`tab-button ${activeTab === 'bulk' ? 'active' : ''}`}
          onClick={() => setActiveTab('bulk')}
        >
          Bulk Management
        </button>
        <button
          className={`tab-button ${activeTab === 'individual' ? 'active' : ''}`}
          onClick={() => setActiveTab('individual')}
        >
          By Store
        </button>
      </div>

      {activeTab === 'individual' && (
        <div className="store-lookup-container">
          <div className="store-lookup-search-wrapper">
            <div className="store-lookup-search">
              <div className="search-input-wrapper">
                <input
                  type="text"
                  placeholder="Type company code or name (e.g., WFC, Walmart)..."
                  value={selectedLookupCompany ? '' : storeLookupSearch}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase();
                    setStoreLookupSearch(value);
                  }}
                  onFocus={handleInputFocus}
                  className="store-lookup-input"
                  disabled={loadingStores || loadingConfigs || selectedLookupCompany}
                />
                {selectedLookupCompany && (
                  <div className="selected-company-overlay">
                    <span className="selected-company-text">
                      {selectedLookupCompany.companyName} ({selectedLookupCompany.companyCode})
                    </span>
                    <button className="clear-company-x-inside" onClick={clearCompanySelection} title="Clear selection">✕</button>
                  </div>
                )}
              </div>
            </div>

            {showSuggestions && companySuggestions.length > 0 && !selectedLookupCompany && (
              <div className="store-suggestions-dropdown">
                {companySuggestions.map((company) => (
                  <div
                    key={company.companyCode}
                    className="store-suggestion-item"
                    onClick={() => handleCompanySelectForLookup(company)}
                  >
                    <div className="suggestion-store-code">{company.companyCode}</div>
                    <div className="suggestion-store-name">{company.companyName}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {loadingStores && (
            <div className="loading-message">Loading stores for {selectedLookupCompany?.companyName}...</div>
          )}

          {storesError && (
            <div className="store-lookup-error">{storesError}</div>
          )}

          {loadingConfigs && (
            <div className="loading-message">
              Loading store configurations... {configLoadProgress.current}/{configLoadProgress.total}
            </div>
          )}

          {selectedLookupCompany && storeConfigData.length > 0 && !loadingConfigs && (
            <div className="store-batch-updater">

              {/* Export Button */}
              <div className="store-config-header">
                <button
                  className="export-csv-button"
                  onClick={exportStoreConfigToCSV}
                  title="Export current store configurations to CSV"
                >
                  Export Current Setup
                </button>
              </div>

              {/* Configuration Fields Grid */}
              <div className="config-fields-grid">
                {storeConfigData.length > 0 && storeConfigData[0].fullData && storeConfigData[0].fullData.storeConfig &&
                  Object.keys(storeConfigData[0].fullData.storeConfig).map(configKey => {
                    const sampleValue = storeConfigData[0][configKey];
                    const isBoolean = typeof sampleValue === 'boolean' || configKey.toLowerCase().includes('enable');

                    return (
                      <div key={configKey} className="config-field-item">
                        <label>{getStoreConfigDisplayName(configKey)}:</label>
                        {isBoolean ? (
                          <select
                            value={bulkUpdateValues[configKey] ?? ''}
                            onChange={(e) => setBulkUpdateValues(prev => ({
                              ...prev,
                              [configKey]: e.target.value === '' ? '' : parseInt(e.target.value)
                            }))}
                          >
                            <option value="">-- Select --</option>
                            <option value="1">Enabled</option>
                            <option value="0">Disabled</option>
                          </select>
                        ) : typeof sampleValue === 'string' ? (
                          <input
                            type="text"
                            value={bulkUpdateValues[configKey] ?? ''}
                            onChange={(e) => setBulkUpdateValues(prev => ({
                              ...prev,
                              [configKey]: e.target.value
                            }))}
                            placeholder="Enter value..."
                          />
                        ) : (
                          <input
                            type="number"
                            value={bulkUpdateValues[configKey] ?? ''}
                            onChange={(e) => setBulkUpdateValues(prev => ({
                              ...prev,
                              [configKey]: e.target.value === '' ? '' : parseInt(e.target.value)
                            }))}
                            placeholder="Enter value..."
                          />
                        )}
                      </div>
                    );
                  })
                }
              </div>

              {/* Action Buttons */}
              {(() => {
                const hasChanges = Object.keys(bulkUpdateValues).some(key =>
                  bulkUpdateValues[key] !== '' && bulkUpdateValues[key] !== null && bulkUpdateValues[key] !== undefined
                );
                return hasChanges && (
                  <div className="batch-action-buttons">
                    <button
                      className="update-now-button"
                      onClick={handleBulkStoreUpdate}
                      disabled={concurrentUpdating || selectedStores.length === 0}
                    >
                      {concurrentUpdating ? 'Updating...' : 'Update Now'}
                    </button>
                    {!concurrentUpdating && selectedStores.length > 0 && (
                      <span className="status-message-inline">
                        {selectedStores.length} store(s) selected
                      </span>
                    )}
                    {concurrentUpdating && (
                      <span className="status-message-inline updating">
                        Updating stores...
                      </span>
                    )}
                  </div>
                );
              })()}

              {/* Store Table */}
              <div className="store-batch-table-wrapper">
                <table className="store-batch-table">
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }}>
                        <input
                          type="checkbox"
                          checked={selectedStores.length === storeConfigData.length && storeConfigData.length > 0}
                          onChange={handleSelectAllStores}
                        />
                      </th>
                      <th>Store</th>
                      {storeConfigData.length > 0 && storeConfigData[0].fullData && storeConfigData[0].fullData.storeConfig &&
                        Object.keys(storeConfigData[0].fullData.storeConfig).map(configKey => (
                          <th key={configKey}>{getStoreConfigDisplayName(configKey)}</th>
                        ))
                      }
                    </tr>
                  </thead>
                  <tbody>
                    {storeConfigData.map((store) => (
                      <tr key={store.storeCode} className={selectedStores.includes(store.storeCode) ? 'selected-row' : ''}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedStores.includes(store.storeCode)}
                            onChange={() => handleStoreSelection(store.storeCode)}
                          />
                        </td>
                        <td>{store.storeCode}</td>
                        {store.fullData && store.fullData.storeConfig &&
                          Object.keys(store.fullData.storeConfig).map(configKey => {
                            const value = store[configKey];
                            const isBoolean = typeof value === 'boolean' || configKey.toLowerCase().includes('enable');

                            return (
                              <td key={configKey}>
                                {isBoolean
                                  ? (value === 1 || value === true ? 'Enabled' : 'Disabled')
                                  : (value !== null && value !== undefined ? value : '')}
                              </td>
                            );
                          })
                        }
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Keep old individual tab content commented out for reference
      {activeTab === 'individual' && (
        <>
      <div className="search-controls-wrapper">
        <div className="company-selector">
          <div className="search-input">
            {selectedCompany ? (
              // Selected state: Show selected company with clear button
              <div className="selected-company">
                <span className="selected-company-text">
                  {selectedCompany} - {companies.find(c => c.code === selectedCompany)?.name || selectedCompany}
                </span>
                <button
                  className="clear-selection"
                  onClick={clearSelection}
                  type="button"
                  title="Clear selection"
                >
                  ×
                </button>
              </div>
            ) : (
              <div className="search-container">
                <input
                  type="text"
                  placeholder={isParameterSearch ? `${parameterSearchResults.length} stores found matching criteria` : "Search companies (Code - Name)..."}
                  value={searchTerm}
                  onChange={(e) => {
                    console.log('Input changed:', e.target.value);
                    setSearchTerm(e.target.value);
                    setShowDropdown(true);
                  }}
                  onFocus={() => {
                    console.log('Input focused, setting showDropdown to true');
                    setShowDropdown(true);
                  }}
                  onBlur={() => {
                    console.log('Input blurred, hiding dropdown in 200ms');
                    setTimeout(() => setShowDropdown(false), 200);
                  }}
                  disabled={loading}
                  className="search-input"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="clear-search-button"
                    title="Clear search"
                  >
                    ×
                  </button>
                )}
                <div
                  className="dropdown-arrow"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('Arrow clicked, current showDropdown:', showDropdown);
                    setShowDropdown(!showDropdown);
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                  }}
                  style={{position: 'absolute', right: '30px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', zIndex: 10}}
                >
                  <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                    <path d="M1 1L6 6L11 1" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
            )}
            {!selectedCompany && showDropdown && filteredCompanies.length > 0 && (
              <div className="dropdown-list">
                {filteredCompanies.map(company => (
                  <div
                    key={company.code}
                    className="dropdown-item"
                    onMouseDown={() => {
                      handleCompanySelect(company.code, company.name);
                    }}
                  >
                    {company.code} - {company.name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="parameter-controls">
          <button
            className="parameter-search-button"
            onClick={openParameterModal}
            disabled={loading || companies.length === 0}
            title="Search stores by matching parameters"
          >
            Match on Parameters
          </button>
          {isParameterSearch && (
            <button onClick={clearParameterSearch} className="clear-filter-small">
              Clear Filter
            </button>
          )}
        </div>
      </div>

      {searchingParameters && (
        <div className="loading">
          Searching stores by parameters...
        </div>
      )}

      {loading && !searchingParameters && (
        <div className="loading">
          Loading...
        </div>
      )}

      {selectedCompany && Object.keys(functions).length > 0 && (
        <div className="functions-container">
          <div className="functions-header">
            <h2>Functions for {companies.find(c => c.code === selectedCompany)?.name || selectedCompany}</h2>
            {hasChanges && (
              <button
                onClick={handleSave}
                disabled={saving}
                className="save-button"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            )}
          </div>

          <div className="functions-scrollable">
            <div className="functions-grid">
            {Object.entries(categorizedFunctions).map(([category, functionKeys]) => (
              <div key={category} className="function-category">
                <h3>{category}</h3>
                <div className="function-items">
                  {functionKeys.map(functionKey => {
                    if (!(functionKey in functions)) return null;
                    return (
                      <div key={functionKey} className="function-item">
                        <label className="function-label">
                          <span className="function-name">
                            {getDisplayName(functionKey)}
                          </span>
                          <div className="toggle-switch">
                            <input
                              type="checkbox"
                              checked={typeof functions[functionKey] === 'number' ? functions[functionKey] > 0 : functions[functionKey] || false}
                              onChange={() => handleFunctionToggle(functionKey)}
                            />
                            <span className="toggle-slider"></span>
                          </div>
                        </label>

                        {functionKey === 'preserveArticleFiles' && functions[functionKey] && (
                          <div className="conditional-field">
                            <label>
                              <span>Retention Period *</span>
                              <div className="input-with-label">
                                <input
                                  type="number"
                                  value={functions.retentionPeriod || ''}
                                  onChange={(e) => setFunctions(prev => ({ ...prev, retentionPeriod: parseInt(e.target.value) || 0 }))}
                                  className="setting-input"
                                  placeholder="Days"
                                />
                                <span className="input-suffix">Days</span>
                              </div>
                            </label>
                          </div>
                        )}

                        {functionKey === 'articleScheduleAllowedDays' && functions[functionKey] > 0 && (
                          <div className="conditional-field">
                            <label>
                              <span></span>
                              <div className="input-with-label">
                                <input
                                  type="number"
                                  value={functions.articleScheduleAllowedDays || ''}
                                  onChange={(e) => setFunctions(prev => ({ ...prev, articleScheduleAllowedDays: parseInt(e.target.value) || 0 }))}
                                  className="setting-input"
                                  placeholder="Days"
                                />
                                <span className="input-suffix">Days</span>
                              </div>
                            </label>
                          </div>
                        )}

                        {functionKey === 'nfcChanges' && functions[functionKey] && (
                          <div className="conditional-field">
                            <label>
                              <span>NFC Default URL</span>
                              <div className="input-with-counter">
                                <input
                                  type="text"
                                  value={functions.nfcDefaultUrl || ''}
                                  onChange={(e) => {
                                    if (e.target.value.length <= 225) {
                                      setFunctions(prev => ({ ...prev, nfcDefaultUrl: e.target.value }));
                                    }
                                  }}
                                  className="setting-input"
                                  placeholder="e.g., https://www.example.com"
                                  maxLength={225}
                                />
                                <span className="char-counter">{(functions.nfcDefaultUrl || '').length}/225</span>
                              </div>
                            </label>
                          </div>
                        )}

                        {functionKey === 'labelOTAStatus' && functions[functionKey] && (
                          <div className="conditional-field">
                            <div className="date-picker-group">
                              <label>
                                <span>Start date *</span>
                                <input
                                  type="date"
                                  value={functions.labelOTAStartDate || ''}
                                  onChange={(e) => setFunctions(prev => ({ ...prev, labelOTAStartDate: e.target.value }))}
                                  className="setting-input date-input"
                                />
                              </label>
                              <label>
                                <span>End date *</span>
                                <input
                                  type="date"
                                  value={functions.labelOTAEndDate || ''}
                                  onChange={(e) => setFunctions(prev => ({ ...prev, labelOTAEndDate: e.target.value }))}
                                  className="setting-input date-input"
                                />
                              </label>
                            </div>
                          </div>
                        )}

                        {functionKey === 'cameraOTAStatus' && functions[functionKey] && (
                          <div className="conditional-field">
                            <div className="date-picker-group">
                              <label>
                                <span>Start date *</span>
                                <input
                                  type="date"
                                  value={functions.cameraOTAStartDate || ''}
                                  onChange={(e) => setFunctions(prev => ({ ...prev, cameraOTAStartDate: e.target.value }))}
                                  className="setting-input date-input"
                                />
                              </label>
                              <label>
                                <span>End date *</span>
                                <input
                                  type="date"
                                  value={functions.cameraOTAEndDate || ''}
                                  onChange={(e) => setFunctions(prev => ({ ...prev, cameraOTAEndDate: e.target.value }))}
                                  className="setting-input date-input"
                                />
                              </label>
                            </div>
                          </div>
                        )}

                        {functionKey === 'webhooks' && functions[functionKey] && (
                          <div className="conditional-field">
                            <div className="webhook-type-selector">
                              <label className="radio-label">
                                <input
                                  type="radio"
                                  checked={webhookType === 'restAPI'}
                                  onChange={() => handleWebhookTypeChange('restAPI')}
                                />
                                <span>Webhook REST API</span>
                              </label>
                              <label className="radio-label">
                                <input
                                  type="radio"
                                  checked={webhookType === 'azureEventHub'}
                                  onChange={() => handleWebhookTypeChange('azureEventHub')}
                                />
                                <span>Webhook Azure Event Hub</span>
                              </label>
                            </div>
                            <div className="webhook-checkboxes">
                              <label className="checkbox-label">
                                <input
                                  type="checkbox"
                                  checked={webhookCheckboxes.labelStatus}
                                  onChange={() => handleWebhookCheckbox('labelStatus')}
                                />
                                <span>Label Status</span>
                              </label>
                              <label className="checkbox-label">
                                <input
                                  type="checkbox"
                                  checked={webhookCheckboxes.labelButtonPress}
                                  onChange={() => handleWebhookCheckbox('labelButtonPress')}
                                />
                                <span>Label Button Press</span>
                              </label>
                              {webhookType === 'restAPI' && (
                                <label className="checkbox-label">
                                  <input
                                    type="checkbox"
                                    checked={webhookCheckboxes.productReconciliation}
                                    onChange={() => handleWebhookCheckbox('productReconciliation')}
                                  />
                                  <span>Product Reconciliation</span>
                                </label>
                              )}
                            </div>
                          </div>
                        )}

                        {functionKey === 'powerRail' && functions[functionKey] && (
                          <div className="conditional-field">
                            <label className="checkbox-label">
                              <input
                                type="checkbox"
                                checked={functions.powerRailAutoRegistration || false}
                                onChange={() => setFunctions(prev => ({ ...prev, powerRailAutoRegistration: !prev.powerRailAutoRegistration }))}
                              />
                              <span>Power Rail Auto Registration</span>
                            </label>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            </div>
          </div>
        </div>
      )}

      {showParameterModal && (
        <div className="parameter-modal-overlay" onClick={() => setShowParameterModal(false)}>
          <div className="parameter-modal" onClick={(e) => e.stopPropagation()}>
            <div className="parameter-modal-header">
              <h2>Match on Parameters</h2>
              <button className="close-modal" onClick={() => setShowParameterModal(false)}>×</button>
            </div>
            <p className="parameter-modal-description">Select parameters to find stores that have ALL of them enabled</p>
            <div className="parameter-modal-content">
              {Object.entries(categorizedFunctions).map(([category, functionKeys]) => (
                <div key={category} className="parameter-category">
                  <h3>{category}</h3>
                  <div className="parameter-items">
                    {functionKeys.map(functionKey => (
                      <label key={functionKey} className="parameter-checkbox-label">
                        <input
                          type="checkbox"
                          checked={selectedParameters[functionKey] || false}
                          onChange={() => toggleParameter(functionKey)}
                        />
                        <span>{getDisplayName(functionKey)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="parameter-modal-footer">
              <button className="cancel-parameter-search" onClick={() => setShowParameterModal(false)}>
                Cancel
              </button>
              <button className="start-parameter-search" onClick={searchByParameters}>
                Find Companies
              </button>
            </div>
          </div>
        </div>
      )}
      </>
      )} */}

      {activeTab === 'bulk' && (
        <div className="bulk-management-container">
          <div className="bulk-header">
            <div className="bulk-header-left">
              <CompanySelector
                onCompanySelect={() => {}}
                onClear={() => {
                  setSelectedBulkCompanyCodes([]);
                  setBulkSearchText('');
                }}
                placeholder="Search companies..."
                autoFetchStores={false}
                multiSelect={true}
                selectedCompanies={selectedBulkCompanyCodes}
                onMultiSelectChange={(companyCodes) => setSelectedBulkCompanyCodes(companyCodes)}
                onSearchChange={(searchText) => setBulkSearchText(searchText)}
              />
              <button
                className="match-parameters-button"
                onClick={openBulkParameterModal}
                disabled={loadingBulk || bulkData.length === 0}
                title="Search stores by matching parameters"
              >
                Match on Parameters
              </button>
              {filteredBulkData.length > 0 && (
                <button onClick={clearBulkParameterFilter} className="clear-filter-small">
                  Clear Filter ({filteredBulkData.length} stores)
                </button>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                className="export-csv-button"
                onClick={exportToCSV}
                disabled={loadingBulk || bulkData.length === 0}
                title="Export current view to CSV"
              >
                Export to CSV
              </button>
              <button
                className="edit-mode-button"
                onClick={() => setEditMode(!editMode)}
              >
                {editMode ? 'Cancel Edit' : 'Edit'}
              </button>
            </div>
          </div>

          {loadingBulk ? (
            <div className="bulk-loading">Loading all companies data...</div>
          ) : bulkData.length === 0 ? (
            <div className="bulk-empty">No data available. Please ensure companies are loaded.</div>
          ) : (
            <div className="bulk-table-wrapper">
              <table className="bulk-table">
                <thead>
                  <tr>
                    <th className="sticky-col sortable-header" onClick={() => handleSort('code')}>
                      <div className="header-content">
                        <span>Store Code</span>
                        {sortConfig.key === 'code' && (
                          <span className="sort-indicator">{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>
                        )}
                      </div>
                    </th>
                    <th className="sticky-col-2 sortable-header" onClick={() => handleSort('name')}>
                      <div className="header-content">
                        <span>Store Name</span>
                        {sortConfig.key === 'name' && (
                          <span className="sort-indicator">{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>
                        )}
                      </div>
                    </th>
                    <th style={{ width: '50px', textAlign: 'center' }} title="Export company config as JSON">
                      <span style={{ fontSize: '12px' }}>Export</span>
                    </th>
                    {Object.keys(categorizedFunctions).map(category =>
                      categorizedFunctions[category].map(funcKey => {
                        const fieldType = getFieldType(funcKey, null);

                        return (
                          <th key={funcKey} className="sortable-header" onClick={() => handleSort(funcKey)}>
                            <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px'}}>
                              <div className="header-content">
                                <span>{getDisplayName(funcKey)}</span>
                                {sortConfig.key === funcKey && (
                                  <span className="sort-indicator">{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>
                                )}
                              </div>
                              {editMode && fieldType === 'checkbox' && (
                                <input
                                  type="checkbox"
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    const dataToUpdate = (selectedBulkCompanyCodes.length > 0 || bulkSearchText.trim().length >= 2)
                                      ? searchFilteredData
                                      : (filteredBulkData.length > 0 ? filteredBulkData : bulkData);

                                    const newData = [...bulkData];
                                    const newChanges = { ...bulkChanges };

                                    dataToUpdate.forEach(store => {
                                      const idx = newData.findIndex(s => s.code === store.code);
                                      if (idx !== -1) {
                                        newData[idx][funcKey] = e.target.checked;
                                        newChanges[store.code] = {
                                          ...newChanges[store.code],
                                          [funcKey]: e.target.checked
                                        };
                                      }
                                    });

                                    setBulkData(newData);
                                    setBulkChanges(newChanges);
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  title={`Select all for ${getDisplayName(funcKey)}`}
                                  style={{cursor: 'pointer'}}
                                />
                              )}
                            </div>
                          </th>
                        );
                      })
                    )}
                  </tr>
                </thead>
                <tbody>
                  {getSortedData(selectedBulkCompanyCodes.length > 0 || bulkSearchText.trim().length >= 2 ? searchFilteredData : (filteredBulkData.length > 0 ? filteredBulkData : bulkData)).map((store) => (
                    <tr key={store.code} style={failedStores.includes(store.code) ? {backgroundColor: '#fee2e2', border: '1px solid #fecaca'} : {}}>
                      <td className="sticky-col" style={failedStores.includes(store.code) ? {backgroundColor: '#fee2e2'} : {}}>{store.code}</td>
                      <td className="sticky-col-2" style={failedStores.includes(store.code) ? {backgroundColor: '#fee2e2'} : {}}>{store.name}</td>
                      <td style={{ textAlign: 'center', ...failedStores.includes(store.code) ? {backgroundColor: '#fee2e2'} : {} }}>
                        <button
                          onClick={() => exportCompanyConfigJSON(store.code, store.name)}
                          className="export-json-btn"
                          title={`Export ${store.code} config as JSON`}
                        >
                          ⬇
                        </button>
                      </td>
                      {Object.keys(categorizedFunctions).map(category =>
                        categorizedFunctions[category].map(funcKey => {
                          const fieldType = getFieldType(funcKey, store[funcKey]);
                          const showField = shouldShowField(funcKey, store);
                          const isConditional = conditionalFieldParents[funcKey];

                          return (
                            <td
                              key={funcKey}
                              style={{
                                ...(failedStores.includes(store.code) ? {backgroundColor: '#fee2e2'} : {}),
                                ...(isConditional && !showField ? {backgroundColor: '#f5f5f5'} : {})
                              }}
                              title={isConditional && !showField ? `Enable "${getDisplayName(conditionalFieldParents[funcKey])}" to use this field` : ''}
                            >
                              {!showField ? (
                                <span style={{color: '#999', fontSize: '12px', fontStyle: 'italic'}}>N/A</span>
                              ) : editMode ? (
                                fieldType === 'checkbox-number' ? (
                                  <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                                    <input
                                      type="checkbox"
                                      checked={typeof store[funcKey] === 'number' ? store[funcKey] > 0 : store[funcKey]}
                                      onChange={(e) => {
                                        const newData = [...bulkData];
                                        const idx = newData.findIndex(s => s.code === store.code);
                                        // Get the associated day field
                                        const dayField = funcKey === 'preserveArticleFiles' ? 'retentionPeriod' : 'articleScheduleAllowedDays';

                                        if (e.target.checked) {
                                          newData[idx][funcKey] = typeof store[funcKey] === 'number' ? 1 : true;
                                          // Initialize day field if not set
                                          if (!newData[idx][dayField] || newData[idx][dayField] === 0) {
                                            newData[idx][dayField] = 7; // Default to 7 days
                                          }
                                        } else {
                                          newData[idx][funcKey] = typeof store[funcKey] === 'number' ? 0 : false;
                                          newData[idx][dayField] = 0;
                                        }

                                        setBulkData(newData);
                                        setBulkChanges({...bulkChanges, [store.code]: newData[idx]});
                                      }}
                                      title={`Enable ${getDisplayName(funcKey)}`}
                                    />
                                    {(typeof store[funcKey] === 'number' ? store[funcKey] > 0 : store[funcKey]) && (
                                      <input
                                        type="number"
                                        min="1"
                                        max="365"
                                        value={funcKey === 'preserveArticleFiles' ? (store.retentionPeriod || 7) : (store.articleScheduleAllowedDays || 7)}
                                        onChange={(e) => {
                                          const newData = [...bulkData];
                                          const idx = newData.findIndex(s => s.code === store.code);
                                          const val = parseInt(e.target.value) || 1;
                                          const dayField = funcKey === 'preserveArticleFiles' ? 'retentionPeriod' : 'articleScheduleAllowedDays';
                                          newData[idx][dayField] = Math.max(1, Math.min(365, val));
                                          setBulkData(newData);
                                          setBulkChanges({...bulkChanges, [store.code]: newData[idx]});
                                        }}
                                        className="bulk-input"
                                        style={{width: '60px', fontSize: '12px', textAlign: 'center', padding: '2px 4px'}}
                                        placeholder="Days"
                                        title={`Days for ${getDisplayName(funcKey)}`}
                                      />
                                    )}
                                  </div>
                                ) : fieldType === 'checkbox-text' ? (
                                  <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                                    <input
                                      type="checkbox"
                                      checked={typeof store[funcKey] === 'number' ? store[funcKey] > 0 : store[funcKey]}
                                      onChange={(e) => {
                                        const newData = [...bulkData];
                                        const idx = newData.findIndex(s => s.code === store.code);

                                        if (e.target.checked) {
                                          newData[idx][funcKey] = typeof store[funcKey] === 'number' ? 1 : true;
                                        } else {
                                          newData[idx][funcKey] = typeof store[funcKey] === 'number' ? 0 : false;
                                          // Clear the text field when unchecking
                                          newData[idx].nfcDefaultUrl = '';
                                        }

                                        setBulkData(newData);
                                        setBulkChanges({...bulkChanges, [store.code]: newData[idx]});
                                      }}
                                      title={`Enable ${getDisplayName(funcKey)}`}
                                    />
                                    {(typeof store[funcKey] === 'number' ? store[funcKey] > 0 : store[funcKey]) && (
                                      <input
                                        type="text"
                                        value={store.nfcDefaultUrl || ''}
                                        onChange={(e) => {
                                          const newData = [...bulkData];
                                          const idx = newData.findIndex(s => s.code === store.code);
                                          newData[idx].nfcDefaultUrl = e.target.value;
                                          setBulkData(newData);
                                          setBulkChanges({...bulkChanges, [store.code]: newData[idx]});
                                        }}
                                        className="bulk-input"
                                        style={{minWidth: '200px', fontSize: '12px', padding: '2px 4px'}}
                                        placeholder="NFC Default URL"
                                        title="NFC Default URL"
                                      />
                                    )}
                                  </div>
                                ) : fieldType === 'ota-dates' ? (
                                  <div style={{display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap'}}>
                                    <input
                                      type="checkbox"
                                      checked={typeof store[funcKey] === 'number' ? store[funcKey] > 0 : store[funcKey]}
                                      onChange={(e) => {
                                        const newData = [...bulkData];
                                        const idx = newData.findIndex(s => s.code === store.code);
                                        newData[idx][funcKey] = typeof store[funcKey] === 'number' ? (e.target.checked ? 1 : 0) : e.target.checked;
                                        setBulkData(newData);
                                        setBulkChanges({...bulkChanges, [store.code]: newData[idx]});
                                      }}
                                      title={`Enable ${getDisplayName(funcKey)}`}
                                    />
                                    {(typeof store[funcKey] === 'number' ? store[funcKey] > 0 : store[funcKey]) && (
                                      <>
                                        <input
                                          type="date"
                                          value={store[funcKey === 'labelOTAStatus' ? 'labelOTAStartDate' : 'cameraOTAStartDate'] || ''}
                                          onChange={(e) => {
                                            const newData = [...bulkData];
                                            const idx = newData.findIndex(s => s.code === store.code);
                                            const startDateKey = funcKey === 'labelOTAStatus' ? 'labelOTAStartDate' : 'cameraOTAStartDate';
                                            newData[idx][startDateKey] = e.target.value;
                                            setBulkData(newData);
                                            setBulkChanges({...bulkChanges, [store.code]: newData[idx]});
                                          }}
                                          className="bulk-input"
                                          style={{minWidth: '110px', fontSize: '11px'}}
                                          title="Start Date"
                                        />
                                        <span style={{fontSize: '12px'}}>-</span>
                                        <input
                                          type="date"
                                          value={store[funcKey === 'labelOTAStatus' ? 'labelOTAEndDate' : 'cameraOTAEndDate'] || ''}
                                          onChange={(e) => {
                                            const newData = [...bulkData];
                                            const idx = newData.findIndex(s => s.code === store.code);
                                            const endDateKey = funcKey === 'labelOTAStatus' ? 'labelOTAEndDate' : 'cameraOTAEndDate';
                                            newData[idx][endDateKey] = e.target.value;
                                            setBulkData(newData);
                                            setBulkChanges({...bulkChanges, [store.code]: newData[idx]});
                                          }}
                                          className="bulk-input"
                                          style={{minWidth: '110px', fontSize: '11px'}}
                                          title="End Date"
                                        />
                                      </>
                                    )}
                                  </div>
                                ) : fieldType === 'checkbox' ? (
                                  <input
                                    type="checkbox"
                                    checked={typeof store[funcKey] === 'number' ? store[funcKey] > 0 : store[funcKey]}
                                    onChange={(e) => {
                                      const newData = [...bulkData];
                                      const idx = newData.findIndex(s => s.code === store.code);
                                      newData[idx][funcKey] = typeof store[funcKey] === 'number' ? (e.target.checked ? 7 : 0) : e.target.checked;
                                      setBulkData(newData);
                                      setBulkChanges({...bulkChanges, [store.code]: newData[idx]});
                                    }}
                                    title={getDisplayName(funcKey)}
                                  />
                                ) : fieldType === 'date' ? (
                                  <input
                                    type="date"
                                    value={store[funcKey] || ''}
                                    onChange={(e) => {
                                      const newData = [...bulkData];
                                      const idx = newData.findIndex(s => s.code === store.code);
                                      newData[idx][funcKey] = e.target.value;
                                      setBulkData(newData);
                                      setBulkChanges({...bulkChanges, [store.code]: newData[idx]});
                                    }}
                                    className="bulk-input"
                                    style={{minWidth: '130px', fontSize: '12px'}}
                                    placeholder={getPlaceholder(funcKey)}
                                    title={getDisplayName(funcKey)}
                                  />
                                ) : fieldType === 'number-toggle' ? (
                                  <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                                    <input
                                      type="checkbox"
                                      checked={store[funcKey] > 0}
                                      onChange={(e) => {
                                        const newData = [...bulkData];
                                        const idx = newData.findIndex(s => s.code === store.code);
                                        newData[idx][funcKey] = e.target.checked ? 7 : 0;
                                        setBulkData(newData);
                                        setBulkChanges({...bulkChanges, [store.code]: newData[idx]});
                                      }}
                                      title={`Enable ${getDisplayName(funcKey)}`}
                                    />
                                    {store[funcKey] > 0 && (
                                      <input
                                        type="number"
                                        min="1"
                                        max="365"
                                        value={store[funcKey]}
                                        onChange={(e) => {
                                          const newData = [...bulkData];
                                          const idx = newData.findIndex(s => s.code === store.code);
                                          const val = parseInt(e.target.value) || 1;
                                          newData[idx][funcKey] = Math.max(1, Math.min(365, val));
                                          setBulkData(newData);
                                          setBulkChanges({...bulkChanges, [store.code]: newData[idx]});
                                        }}
                                        className="bulk-input"
                                        style={{width: '60px', fontSize: '12px', textAlign: 'center', padding: '2px 4px'}}
                                        placeholder="Days"
                                        title={`Days for ${getDisplayName(funcKey)}`}
                                      />
                                    )}
                                  </div>
                                ) : fieldType === 'number' ? (
                                  <input
                                    type="number"
                                    min="0"
                                    max="365"
                                    value={store[funcKey] === null || store[funcKey] === undefined ? '' : store[funcKey]}
                                    onChange={(e) => {
                                      const newData = [...bulkData];
                                      const idx = newData.findIndex(s => s.code === store.code);
                                      newData[idx][funcKey] = e.target.value === '' ? null : parseInt(e.target.value);
                                      setBulkData(newData);
                                      setBulkChanges({...bulkChanges, [store.code]: newData[idx]});
                                    }}
                                    className="bulk-input"
                                    style={{width: '70px', fontSize: '12px', textAlign: 'center'}}
                                    placeholder={getPlaceholder(funcKey)}
                                    title={getDisplayName(funcKey)}
                                  />
                                ) : (
                                  <input
                                    type="text"
                                    value={store[funcKey] || ''}
                                    onChange={(e) => {
                                      const newData = [...bulkData];
                                      const idx = newData.findIndex(s => s.code === store.code);
                                      newData[idx][funcKey] = e.target.value;
                                      setBulkData(newData);
                                      setBulkChanges({...bulkChanges, [store.code]: newData[idx]});
                                    }}
                                    className="bulk-input"
                                    style={{minWidth: '150px', fontSize: '12px'}}
                                    placeholder={getPlaceholder(funcKey)}
                                    title={getDisplayName(funcKey)}
                                  />
                                )
                              ) : (
                                fieldType === 'ota-dates' ? (
                                  (() => {
                                    const isEnabled = typeof store[funcKey] === 'number' ? store[funcKey] > 0 : store[funcKey];
                                    const startDateKey = funcKey === 'labelOTAStatus' ? 'labelOTAStartDate' : 'cameraOTAStartDate';
                                    const endDateKey = funcKey === 'labelOTAStatus' ? 'labelOTAEndDate' : 'cameraOTAEndDate';
                                    const startDate = store[startDateKey];
                                    const endDate = store[endDateKey];
                                    const formatDate = (dateStr) => {
                                      if (!dateStr) return '';
                                      const date = new Date(dateStr);
                                      return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
                                    };
                                    return (
                                      <span className={isEnabled ? 'status-enabled' : 'status-disabled'}>
                                        {isEnabled
                                          ? `✓${startDate && endDate ? ` ${formatDate(startDate)} - ${formatDate(endDate)}` : ''}`
                                          : '✗'}
                                      </span>
                                    );
                                  })()
                                ) : fieldType === 'checkbox-number' ? (
                                  <span className={(typeof store[funcKey] === 'number' ? store[funcKey] > 0 : store[funcKey]) ? 'status-enabled' : 'status-disabled'}>
                                    {(typeof store[funcKey] === 'number' ? store[funcKey] > 0 : store[funcKey])
                                      ? `✓ (${store.retentionPeriod || 7} days)`
                                      : '✗'}
                                  </span>
                                ) : fieldType === 'checkbox-text' ? (
                                  <span className={(typeof store[funcKey] === 'number' ? store[funcKey] > 0 : store[funcKey]) ? 'status-enabled' : 'status-disabled'}>
                                    {(typeof store[funcKey] === 'number' ? store[funcKey] > 0 : store[funcKey])
                                      ? `✓${store.nfcDefaultUrl ? ` (${store.nfcDefaultUrl})` : ''}`
                                      : '✗'}
                                  </span>
                                ) : fieldType === 'number-toggle' ? (
                                  <span className={store[funcKey] > 0 ? 'status-enabled' : 'status-disabled'}>
                                    {store[funcKey] > 0
                                      ? `✓ (${store[funcKey]} days)`
                                      : '✗'}
                                  </span>
                                ) : fieldType === 'checkbox' ? (
                                  <span className={typeof store[funcKey] === 'number' ? (store[funcKey] > 0 ? 'status-enabled' : 'status-disabled') : (store[funcKey] ? 'status-enabled' : 'status-disabled')}>
                                    {typeof store[funcKey] === 'number' ? (store[funcKey] > 0 ? '✓' : '✗') : (store[funcKey] ? '✓' : '✗')}
                                  </span>
                                ) : (
                                  <span style={{fontSize: '12px', color: store[funcKey] ? '#333' : '#999'}}>
                                    {store[funcKey] || '-'}
                                  </span>
                                )
                              )}
                            </td>
                          );
                        })
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {editMode && Object.keys(bulkChanges).length > 0 && (
            <div className="bulk-save-bar">
              <span>{Object.keys(bulkChanges).length} store(s) modified</span>
              <button
                className="bulk-save-button"
                onClick={() => setShowBulkConfirmModal(true)}
                disabled={bulkSaving}
              >
                {bulkSaving ? 'Saving...' : 'Save All Changes'}
              </button>
            </div>
          )}

          {searchingBulkParameters && (
            <div className="loading">
              Filtering stores by parameters...
            </div>
          )}
        </div>
      )}

      {showBulkParameterModal && (
        <div className="parameter-modal-overlay" onClick={() => setShowBulkParameterModal(false)}>
          <div className="parameter-modal" onClick={(e) => e.stopPropagation()}>
            <div className="parameter-modal-header">
              <h2>Match on Parameters</h2>
              <button className="close-modal" onClick={() => setShowBulkParameterModal(false)}>×</button>
            </div>
            <div className="parameter-search-mode-toggle">
              <span className="parameter-mode-label">Find stores where selected parameters are:</span>
              <div className="parameter-mode-buttons">
                <button
                  className={`parameter-mode-btn ${parameterSearchMode === 'enabled' ? 'active' : ''}`}
                  onClick={() => setParameterSearchMode('enabled')}
                >
                  ✓ Enabled
                </button>
                <button
                  className={`parameter-mode-btn ${parameterSearchMode === 'disabled' ? 'active' : ''}`}
                  onClick={() => setParameterSearchMode('disabled')}
                >
                  ✗ Disabled
                </button>
              </div>
            </div>
            <div className="parameter-modal-content">
              {Object.entries(categorizedFunctions).map(([category, functionKeys]) => (
                <div key={category} className="parameter-category">
                  <h3>{category}</h3>
                  <div className="parameter-items">
                    {functionKeys.map(functionKey => (
                      <label key={functionKey} className="parameter-checkbox-label">
                        <input
                          type="checkbox"
                          checked={bulkSelectedParameters[functionKey] || false}
                          onChange={() => toggleBulkParameter(functionKey)}
                        />
                        <span>{getDisplayName(functionKey)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="parameter-modal-footer">
              <button className="cancel-parameter-search" onClick={() => setShowBulkParameterModal(false)}>
                Cancel
              </button>
              <button className="start-parameter-search" onClick={searchBulkByParameters}>
                Find Companies
              </button>
            </div>
          </div>
        </div>
      )}

      {showBulkConfirmModal && (
        <div className="parameter-modal-overlay" onClick={() => setShowBulkConfirmModal(false)}>
          <div className="parameter-modal" onClick={(e) => e.stopPropagation()}>
            <div className="parameter-modal-header">
              <h2>Confirm Bulk Changes</h2>
              <button className="close-modal" onClick={() => setShowBulkConfirmModal(false)}>×</button>
            </div>
            <div className="parameter-modal-content" style={{maxHeight: '400px', overflowY: 'auto'}}>
              <p style={{marginBottom: '16px', color: '#64748b'}}>You are about to save changes for {Object.keys(bulkChanges).length} store(s):</p>
              {Object.entries(bulkChanges).map(([storeCode, storeData]) => {
                const originalStore = originalBulkData.find(s => s.code === storeCode);
                const changes = [];
                if (originalStore) {
                  Object.keys(storeData).forEach(key => {
                    if (key !== 'code' && key !== 'name' && storeData[key] !== originalStore[key]) {
                      const oldVal = originalStore[key];
                      const newVal = storeData[key];
                      const formatVal = (val) => {
                        if (typeof val === 'boolean') return val ? 'ON' : 'OFF';
                        if (typeof val === 'number') return val > 0 ? 'ON' : 'OFF';
                        return val;
                      };
                      changes.push({
                        name: getDisplayName(key),
                        from: formatVal(oldVal),
                        to: formatVal(newVal)
                      });
                    }
                  });
                }
                return (
                  <div key={storeCode} style={{marginBottom: '16px', padding: '14px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0'}}>
                    <div style={{fontWeight: '600', marginBottom: '10px', color: '#1e293b', fontSize: '14px'}}>
                      {storeCode} - {storeData.name}
                    </div>
                    <div style={{fontSize: '12px', color: '#64748b', marginBottom: '8px'}}>
                      <strong>{changes.length}</strong> parameter(s) modified:
                    </div>
                    {changes.map((change, idx) => (
                      <div key={idx} style={{fontSize: '12px', color: '#475569', marginLeft: '8px', marginBottom: '4px'}}>
                        • <strong>{change.name}</strong>: {change.from} → {change.to}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
            <div className="parameter-modal-footer">
              <button className="cancel-parameter-search" onClick={() => setShowBulkConfirmModal(false)}>
                Cancel
              </button>
              <button className="start-parameter-search" onClick={handleBulkSave}>
                Confirm & Save
              </button>
            </div>
          </div>
        </div>
      )}

      {showSuccessModal && (
        <div className="parameter-modal-overlay" style={{background: 'rgba(0, 0, 0, 0.5)'}}>
          <div className="parameter-modal" style={{maxWidth: '400px', textAlign: 'center', padding: '40px 30px'}} onClick={(e) => e.stopPropagation()}>
            <div style={{marginBottom: '20px'}}>
              <svg width="80" height="80" viewBox="0 0 80 80" style={{margin: '0 auto'}}>
                <circle cx="40" cy="40" r="36" fill="#10b981" stroke="#059669" strokeWidth="2"/>
                <path d="M25 40 L35 50 L55 30" stroke="white" strokeWidth="6" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2 style={{color: '#10b981', marginBottom: '10px', fontSize: '24px', fontWeight: '700'}}>Success!</h2>
            <p style={{color: '#64748b', fontSize: '16px', margin: 0}}>
              Successfully updated <strong>{successCount}</strong> store{successCount !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
      )}

      {showFailureModal && (
        <div className="parameter-modal-overlay" style={{background: 'rgba(0, 0, 0, 0.5)'}}>
          <div className="parameter-modal" style={{maxWidth: '400px', textAlign: 'center', padding: '40px 30px'}} onClick={(e) => e.stopPropagation()}>
            <div style={{marginBottom: '20px'}}>
              <svg width="80" height="80" viewBox="0 0 80 80" style={{margin: '0 auto'}}>
                <circle cx="40" cy="40" r="36" fill="#ef4444" stroke="#dc2626" strokeWidth="2"/>
                <path d="M28 28 L52 52 M52 28 L28 52" stroke="white" strokeWidth="6" strokeLinecap="round"/>
              </svg>
            </div>
            <h2 style={{color: '#ef4444', marginBottom: '10px', fontSize: '24px', fontWeight: '700'}}>Error!</h2>
            <p style={{color: '#64748b', fontSize: '16px', margin: 0}}>
              {failureData.success > 0 ? (
                <>Updated <strong>{failureData.success}</strong> store{failureData.success !== 1 ? 's' : ''}, failed to update <strong>{failureData.failed}</strong> store{failureData.failed !== 1 ? 's' : ''}</>
              ) : (
                <>Failed to update <strong>{failureData.failed}</strong> store{failureData.failed !== 1 ? 's' : ''}</>
              )}
            </p>
            {failedStores.length > 0 && (
              <div style={{marginTop: '16px', fontSize: '14px', color: '#6b7280'}}>
                Failed stores are highlighted in red
              </div>
            )}
          </div>
        </div>
      )}
    </div>
    </ProtectedComponent>
  );
};

export default FunctionAssignment;